"use client";

import Image from "next/image";
import { getRankBandConfig } from "./utils";
import { cn } from "@ui/lib";

interface RankBadgeProps {
  rank: number | null;
  engine?: 'google' | 'brave';
  url?: string | null;
  size?: 'sm' | 'md';
  showIcon?: boolean;
  className?: string;
}

export function RankBadge({ 
  rank, 
  engine, 
  url, 
  size = 'sm',
  showIcon = true,
  className 
}: RankBadgeProps) {
  const config = getRankBandConfig(rank);
  const isUnranked = rank === null || rank > 30;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
  };

  const iconSize = size === 'sm' ? 12 : 14;

  const badge = (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        config.bgColor,
        config.color,
        sizeClasses[size],
        className
      )}
    >
      {showIcon && engine && (
        <Image
          src={engine === 'google' ? '/images/google-logo.svg' : '/images/brave-logo.svg'}
          alt={engine === 'google' ? 'Google' : 'Brave'}
          width={iconSize}
          height={iconSize}
          className="flex-shrink-0"
        />
      )}
      {isUnranked ? 'Not ranked' : `#${rank}`}
    </span>
  );

  if (url && !isUnranked) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-80 transition-opacity"
      >
        {badge}
      </a>
    );
  }

  return badge;
}

interface DualRankBadgeProps {
  rankGoogle: number | null;
  urlGoogle?: string | null;
  rankBrave: number | null;
  urlBrave?: string | null;
  /** When true, show Google as N/A (Claude-only search — Claude grounds in Brave). */
  googleUnavailable?: boolean;
  /** When true, show Brave as N/A (no Claude platform produced this search). */
  braveUnavailable?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

function EngineNotAvailable({ engine, size }: { engine: 'Google' | 'Brave'; size: 'sm' | 'md' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium text-gray-500 bg-gray-100 text-xs px-2 py-0.5',
        size === 'md' && 'text-sm px-2.5 py-1'
      )}
      title={`${engine} rank not checked for this grounding source`}
    >
      {engine} N/A
    </span>
  );
}

export function DualRankBadge({
  rankGoogle,
  urlGoogle,
  rankBrave,
  urlBrave,
  googleUnavailable = false,
  braveUnavailable = false,
  size = 'sm',
  className,
}: DualRankBadgeProps) {
  const hasGoogle = rankGoogle !== null && !googleUnavailable;
  const hasBrave = rankBrave !== null && !braveUnavailable;

  if (!hasGoogle && !hasBrave && !googleUnavailable && !braveUnavailable) {
    return (
      <span className={cn('text-xs text-gray-400 italic', className)}>
        Not ranked
      </span>
    );
  }

  return (
    <div className={cn('flex items-center gap-1.5 flex-wrap', className)}>
      {googleUnavailable ? (
        <EngineNotAvailable engine="Google" size={size} />
      ) : hasGoogle ? (
        <RankBadge rank={rankGoogle} engine="google" url={urlGoogle} size={size} />
      ) : null}
      {braveUnavailable ? (
        <EngineNotAvailable engine="Brave" size={size} />
      ) : hasBrave ? (
        <RankBadge rank={rankBrave} engine="brave" url={urlBrave} size={size} />
      ) : null}
    </div>
  );
}


